import { useTheme } from '@emotion/react';
import { Box, Button, Column, Icon, Row } from '@sds-system/components';
import type { Theme } from '@sds-system/types';
import { deepMerge, useGetInternalProps } from '@sds-system/utils';
import pick from 'lodash.pick';
import { useMediaQuery } from 'usehooks-ts';
import { Modal, modalOptionsKey } from '../Modal';
import {
  StyledAlertModalContentWrapper,
  StyledAlertModalRoot,
  StyledAlertModalTitleWrapper,
} from './AlertModal.styled';
import type { AlertModalProps } from './AlertModal.types';

export const AlertModal = (props: AlertModalProps) => {
  const theme = useTheme() as Theme;
  const isSmallDevice = useMediaQuery(`(max-width: ${theme.breakpoint.tablet}px)`);
  const internalProps = useGetInternalProps('AlertModal');
  const defaultIconSource = internalProps?.defaultIconSource;
  const iconColor = props.iconColor || internalProps?.iconColor;
  const { title, ...modalProps } = pick(props, modalOptionsKey);

  const {
    actionButtonText,
    buttonText = '확인',
    content,
    iconSource = defaultIconSource,
    hasIcon = false,
    onClickActionButton,
    onClickButton,
    footer,
    styledComponents,
    grayText,
  } = props;

  const mergedStyledComponentsProps = deepMerge(
    internalProps?.styledComponents || {},
    styledComponents || {},
  );

  const innerIsResponsive = (() => {
    // 직접 전달된 isResponsive가 있으면 최우선 적용
    if (typeof modalProps.isResponsive !== 'undefined') {
      return modalProps.isResponsive;
    }

    // 테마에서 설정된 값이 있으면 그 다음 우선순위
    if (typeof internalProps?.isResponsive !== 'undefined') {
      return internalProps.isResponsive;
    }

    // 아무 설정이 없으면 기본값 true
    return true;
  })();

  const buttonSize = innerIsResponsive && !isSmallDevice ? 'XL' : 'L';

  if (hasIcon && !iconSource)
    throw Error(
      `If the 'hasICon' property is true, either 'defaultIconSource' or 'iconSource' is required. The issue can be resolved in the following ways:\n
      - Set defaultIconSource in the defaultProps of AlertModal in the theme.
      - Set the IconSource value in the AlertModal component.
      `,
    );

  return (
    <Modal
      {...modalProps}
      styledComponents={{
        root: {
          height: ['auto'],
          ...(mergedStyledComponentsProps?.modalRoot && mergedStyledComponentsProps.modalRoot),
        },
      }}
    >
      <StyledAlertModalRoot
        {...(mergedStyledComponentsProps?.alertRoot && mergedStyledComponentsProps.alertRoot)}
      >
        {hasIcon && (
          <Icon
            color={iconColor}
            height={32}
            mb={16}
            minHeight={32}
            minWidth={32}
            source={iconSource!}
            width={32}
          />
        )}
        {title && (
          <StyledAlertModalTitleWrapper
            isResponsive={innerIsResponsive}
            mb={8}
            {...(mergedStyledComponentsProps?.title && mergedStyledComponentsProps.title)}
          >
            {title}
          </StyledAlertModalTitleWrapper>
        )}
        <StyledAlertModalContentWrapper
          gap={8}
          isResponsive={innerIsResponsive}
          textAlign={'center'}
          {...(mergedStyledComponentsProps?.content && mergedStyledComponentsProps.content)}
        >
          {content}
          {grayText && (
            <Column bgColor='bg.secondary' br={8} p={16} width='100%'>
              {grayText.map((text, index) => (
                <Row
                  alignItems='center'
                  fontType='body2.regular'
                  gap={16}
                  justifyContent='space-between'
                  // biome-ignore lint/suspicious/noArrayIndexKey: unique값 없음
                  key={index}
                  width='100%'
                  {...(text.wrapperStyle && text.wrapperStyle)}
                >
                  {text?.left && (
                    <Box css={{ textAlign: text?.right ? 'left' : 'center' }} width='100%'>
                      {text.left}
                    </Box>
                  )}
                  {text?.right && (
                    <Box css={{ textAlign: text?.left ? 'right' : 'center' }} width='100%'>
                      {text.right}
                    </Box>
                  )}
                </Row>
              ))}
            </Column>
          )}
        </StyledAlertModalContentWrapper>
        {actionButtonText && (
          <Button
            onClick={onClickActionButton}
            size={'S'}
            styledComponents={{
              root: {
                mt: 16,
              },
            }}
            variant={'ghost.primary'}
            {...(mergedStyledComponentsProps?.actionButton &&
              mergedStyledComponentsProps.actionButton)}
          >
            {actionButtonText}
          </Button>
        )}
        {buttonText && (
          <Button
            onClick={onClickButton}
            size={buttonSize}
            styledComponents={{
              root: {
                width: '100%',
                mt: 24,
              },
            }}
            variant={'solid.primary'}
            {...(mergedStyledComponentsProps?.button && mergedStyledComponentsProps.button)}
          >
            {buttonText}
          </Button>
        )}
        {footer}
      </StyledAlertModalRoot>
    </Modal>
  );
};
